/**
 * Whole-word matching that works outside ASCII.
 *
 * `\b` is defined on `[A-Za-z0-9_]` even under the `u` flag, so it has no edge next to a
 * symbol ("C++", "Dr.") or an accented letter ("café"), and a whole-word search for any of
 * them found nothing. These lookarounds ask the question `\b` was meant to: is the
 * neighbouring character part of a word? A word character is a letter, digit or combining
 * mark in any script, or an underscore.
 *
 * Patterns built with these must be compiled with the `u` flag.
 */
const WORD_CHAR = "\\p{L}\\p{N}\\p{M}_";

/** Not preceded / followed by a word character. */
export const NOT_AFTER_WORD_CHAR = `(?<![${WORD_CHAR}])`;
export const NOT_BEFORE_WORD_CHAR = `(?![${WORD_CHAR}])`;

/**
 * As above, and also refuses to start or end inside a contraction or hyphenated word:
 * an apostrophe or hyphen with a letter on both sides is part of the word, so removing
 * "it" cannot strip it out of "it's" and leave "'s" behind.
 */
export const NOT_AFTER_WORD_PART = `${NOT_AFTER_WORD_CHAR}(?<![${WORD_CHAR}]['’-])`;
export const NOT_BEFORE_WORD_PART = `${NOT_BEFORE_WORD_CHAR}(?!['’-][${WORD_CHAR}])`;

/** Wrap an already-escaped pattern so it matches only as a whole word. */
export function wholeWordSource(escaped: string): string {
  return `${NOT_AFTER_WORD_CHAR}(?:${escaped})${NOT_BEFORE_WORD_CHAR}`;
}

/** Whole-word wrapper that keeps contractions and hyphenated words intact. */
export function wholeWordPartSource(escaped: string): string {
  return `${NOT_AFTER_WORD_PART}(?:${escaped})${NOT_BEFORE_WORD_PART}`;
}
